/**
 * Firestore data model — Phase 0 (spec section 3).
 *
 * These types describe the *shape of documents as stored in Firestore*. They
 * are deliberately free of Firestore SDK types so they can be shared between
 * the web client (modular Web SDK) and Cloud Functions (Admin SDK), which use
 * different `Timestamp` classes. Timestamps are represented as `TimestampLike`
 * — both SDKs' `Timestamp` satisfy this, and serialized JSON does too.
 */
/** Conservative defaults from spec §3.1, applied when a tenant has none set. */
export const DEFAULT_DELIVERABILITY = {
    bounceWarn: 0.03,
    bouncePause: 0.05,
    complaintWarn: 0.0008,
    complaintPause: 0.001,
};
/** Sensible self-hosted warmup defaults (spec §5.1 ramp shape). */
export const DEFAULT_WARMUP_SETTINGS = {
    startVolume: 4,
    dailyIncrement: 2,
    maxDailyVolume: 40,
    replyRate: 0.3,
};
/** All-zero last-7 warmup activity. */
export const ZERO_WARMUP_LAST7 = {
    sent: 0,
    received: 0,
    replied: 0,
    spamRescued: 0,
};
/** All-zero warmup metrics; the starting value when warmup is first enabled. */
export const ZERO_WARMUP_METRICS = {
    sentTotal: 0,
    receivedTotal: 0,
    repliedTotal: 0,
    spamRescuedTotal: 0,
    last7: { ...ZERO_WARMUP_LAST7 },
};
/** The starting warmup state when a user enables warmup on a mailbox. */
export function freshWarmup() {
    return {
        enabled: true,
        day: 0,
        dailyTarget: 0,
        sentToday: 0,
        status: 'warming',
        metrics: { ...ZERO_WARMUP_METRICS, last7: { ...ZERO_WARMUP_LAST7 } },
    };
}
/**
 * The effective role of a mailbox, defaulting an absent `role` to 'sending' so
 * pre-existing mailboxes (written before warmup-only existed) behave exactly as
 * before. Use this everywhere instead of reading `mailbox.role` directly.
 */
export function mailboxRole(m) {
    return m.role ?? 'sending';
}
/** True when a mailbox is warmup-only and so can never send campaigns. */
export function isWarmupOnly(m) {
    return mailboxRole(m) === 'warmup_only';
}
/** Default enrichment freshness TTL in days (spec §4.3). */
export const ENRICHMENT_TTL_DAYS = 14;
/**
 * The domain for a contact email: everything after the last '@', lowercased and
 * trimmed (spec §4.1, §6.1). Single source of truth, used by the enrichment
 * engine and the merge-variable computation so they never disagree.
 */
export function emailDomain(email) {
    const at = email.lastIndexOf('@');
    return (at >= 0 ? email.slice(at + 1) : email).trim().toLowerCase();
}
/** Public PhishFence scorecard URL for a domain. Needs no PhishFence change. */
export function phishfenceReportUrl(domain) {
    return `https://phishfence.io/scorecard/${domain}`;
}
/**
 * Whether an enrichment snapshot is fresh (within TTL) relative to `now` (spec
 * §4.3, §7.2). A snapshot older than the TTL is stale and re-checked before it
 * is used to assert a factual claim. Pure so it is unit-testable.
 */
export function isEnrichmentFresh(fetchedAt, now, ttlDays = ENRICHMENT_TTL_DAYS) {
    if (!fetchedAt)
        return false;
    const fetchedMs = fetchedAt.seconds * 1000 + fetchedAt.nanoseconds / 1e6;
    const ageMs = now.getTime() - fetchedMs;
    if (ageMs < 0)
        return true; // clock skew: treat a future stamp as fresh
    return ageMs <= ttlDays * 86_400_000;
}
/** The starting value for a campaign's denormalized stats. */
export const ZERO_CAMPAIGN_STATS = {
    sent: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    bounced: 0,
    complained: 0,
    unsubscribed: 0,
    repliedPositive: 0,
};
/**
 * Header stamped on every campaign send carrying the deterministic message id.
 * The reconciler (reconcileSends) uses it to locate a sent copy in the mailbox
 * when the worker sent the mail but died before recording the provider ids.
 */
export const CAMPAIGN_MESSAGE_MARKER_HEADER = 'X-RevBase-Message-Id';
/**
 * The step order encoded as the trailing segment of a message doc id
 * (`${campaignId}_${enrollmentId}_${currentStep}`), used to credit per-step
 * rollups without a step lookup. Returns undefined for an unexpected id shape.
 */
export function stepOrderFromMessageId(messageId) {
    const last = messageId.split('_').pop();
    const n = last === undefined ? NaN : Number(last);
    return Number.isInteger(n) ? n : undefined;
}
/** Max stored plain-text body length for a thread message (INBOX_SPEC §7). */
export const THREAD_BODY_MAX_CHARS = 100_000;
/** Max length of the denormalized snippet shown in the inbox list. */
export const THREAD_SNIPPET_MAX_CHARS = 200;
/**
 * Whether a contact is blocked from receiving a human reply (INBOX_SPEC §5,
 * step 3). Non-bypassable: either the contact doc is flagged suppressed, or a
 * suppression doc exists for its email. Pure so it is unit-testable.
 */
export function isContactSuppressed(contactSuppressed, suppressionDocExists) {
    return contactSuppressed === true || suppressionDocExists;
}
/**
 * Build a "Re:" subject without stacking prefixes: a subject already starting
 * with "Re:" (any case, optional whitespace) is returned unchanged. Pure.
 */
export function replySubject(subject) {
    const trimmed = subject.trim();
    return /^re:\s*/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}
/**
 * Clamp text to a snippet of at most `max` chars, collapsing whitespace so the
 * inbox list stays single-line. Pure.
 */
export function makeSnippet(text, max = THREAD_SNIPPET_MAX_CHARS) {
    const collapsed = text.replace(/\s+/g, ' ').trim();
    return collapsed.length <= max ? collapsed : collapsed.slice(0, max);
}
/** All-zero rollup counters; used when materializing a fresh rollup doc. */
export const ZERO_ROLLUP_COUNTS = {
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    repliedPositive: 0,
    bounced: 0,
    complained: 0,
    unsubscribed: 0,
};
/**
 * Format a Date as a "YYYYMMDD" local-day key in the given IANA timezone.
 * Pure and tz-correct (the rollup's local day is the tenant sendingTimezone).
 */
export function rollupDayKey(at, tz) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    // en-CA yields "YYYY-MM-DD"; strip the dashes for the compact key.
    return fmt.format(at).replace(/-/g, '');
}
/**
 * Deterministic rollup doc id for a (scope, key, day), matching the encoding in
 * spec §2.2 so reads can reconstruct ids for a known day range without an index:
 *   campaign : `{campaignId}_{day}`
 *   step     : `{campaignId}_step{order}_{day}`
 *   mailbox  : `mb_{mailboxId}_{day}`
 *   account  : `acct_{day}`
 */
export function rollupId(args) {
    switch (args.scope) {
        case 'campaign':
            return `${args.campaignId}_${args.day}`;
        case 'step':
            return `${args.campaignId}_step${args.stepOrder}_${args.day}`;
        case 'mailbox':
            return `mb_${args.mailboxId}_${args.day}`;
        case 'account':
            return `acct_${args.day}`;
    }
}
function pct(n) {
    return `${(n * 100).toFixed(2)}%`;
}
/**
 * Pure derivation of a mailbox's health state from its rolling rates, auth, and
 * ramp/pause flags (spec §3.1). The ordering encodes precedence:
 *   1. an already-paused mailbox (manual or prior auto-pause) stays paused
 *   2. a rate over the HARD line forces paused + shouldAutoPause
 *   3. warming takes display priority over a clean healthy (explains low volume)
 *   4. any soft-warning condition is at_risk
 *   5. otherwise healthy
 * Thresholds are passed in so they stay tenant-configurable.
 */
export function deriveHealthState(input, thresholds) {
    const { bounceRate7d, complaintRate7d, authStatus, warming, paused } = input;
    const overBouncePause = bounceRate7d >= thresholds.bouncePause;
    const overComplaintPause = complaintRate7d >= thresholds.complaintPause;
    // (2) Hard line: auto-pause regardless of prior state.
    if (overBouncePause || overComplaintPause) {
        const reason = overBouncePause
            ? `Auto-paused: 7-day bounce rate ${pct(bounceRate7d)} exceeded the ${pct(thresholds.bouncePause)} limit.`
            : `Auto-paused: 7-day complaint rate ${pct(complaintRate7d)} exceeded the ${pct(thresholds.complaintPause)} limit.`;
        return { state: 'paused', reason, shouldAutoPause: !paused };
    }
    // (1) Already paused (manual or prior auto-pause) and not over the hard line:
    // stay paused until a human resumes (spec §3.2: resume is manual only).
    if (paused) {
        return {
            state: 'paused',
            reason: 'Paused. Resume manually to continue sending.',
            shouldAutoPause: false,
        };
    }
    const overBounceWarn = bounceRate7d >= thresholds.bounceWarn;
    const overComplaintWarn = complaintRate7d >= thresholds.complaintWarn;
    const authBad = authStatus === 'warn' || authStatus === 'fail';
    // (3) Warming takes display priority (explains the limited volume), unless a
    // soft-warning condition is present, which the user should see first.
    if (warming && !overBounceWarn && !overComplaintWarn && !authBad) {
        return {
            state: 'warming',
            reason: 'Ramping volume. Sending at a limited cap while warming up.',
            shouldAutoPause: false,
        };
    }
    // (4) Soft-warning band.
    if (overBounceWarn || overComplaintWarn || authBad) {
        let reason;
        if (overBounceWarn) {
            reason = `At risk: 7-day bounce rate ${pct(bounceRate7d)} is at or above the ${pct(thresholds.bounceWarn)} warning line.`;
        }
        else if (overComplaintWarn) {
            reason = `At risk: 7-day complaint rate ${pct(complaintRate7d)} is at or above the ${pct(thresholds.complaintWarn)} warning line.`;
        }
        else {
            reason =
                'At risk: domain authentication (SPF/DKIM/DMARC) needs attention.';
        }
        return { state: 'at_risk', reason, shouldAutoPause: false };
    }
    // (5) Healthy.
    return {
        state: 'healthy',
        reason: 'Sending normally. Bounce, complaint, and auth checks all pass.',
        shouldAutoPause: false,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Self-hosted warmup logic (Phase B): ramp target + peer selection (pure)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Today's warmup send target from the ramp curve (spec §5.1 shape):
 *   target(day) = clamp(startVolume + day * dailyIncrement, 0, maxDailyVolume)
 * Day 0 sends `startVolume`, each day adds `dailyIncrement`, capped at
 * `maxDailyVolume`. Negative ramp days clamp to day 0; the result is a
 * non-negative integer. Pure so it is unit-testable without Firestore.
 */
export function warmupTargetForDay(day, settings) {
    const d = Math.max(0, Math.floor(day));
    const raw = settings.startVolume + d * settings.dailyIncrement;
    const capped = Math.min(raw, settings.maxDailyVolume);
    return Math.max(0, Math.floor(capped));
}
/**
 * Choose `count` distinct warmup peers for a sender from the pool, excluding the
 * sender itself, rotating so the same peer is not always picked first.
 *
 * Peers are every OTHER warmup-enabled mailbox across the system (the pool is
 * passed in already filtered to enabled mailboxes; this function only excludes
 * self and rotates). Phase 0 is one tenant, but the pool spans tenants so the
 * engine is multi-tenant ready, matching the existing engines.
 *
 * Rotation is deterministic from `rotation` (e.g. the sender's warmup day or
 * send index) so successive calls fan out across the pool instead of hammering
 * one peer. When fewer than `count` distinct peers exist, peers are reused in
 * round-robin order (a small pool still warms, just with repeats). Returns an
 * empty array when the sender has NO available peers, so the caller maps that to
 * the `no_peers` status and does nothing (never fabricates a recipient).
 */
export function selectWarmupPeers(selfKey, pool, count, rotation) {
    const peers = pool.filter((p) => p.key !== selfKey);
    if (peers.length === 0 || count <= 0)
        return [];
    // Stable order by key so rotation is reproducible regardless of input order.
    const ordered = [...peers].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const start = ((Math.floor(rotation) % ordered.length) + ordered.length) % ordered.length;
    const out = [];
    for (let i = 0; i < count; i++) {
        out.push(ordered[(start + i) % ordered.length]);
    }
    return out;
}
/** Subject tag that also marks a message as warmup (belt-and-suspenders with the header). */
export const WARMUP_SUBJECT_TAG = '[rb-wu]';
/** Custom header name set on every outbound warmup message. */
export const WARMUP_HEADER_NAME = 'X-RevBase-Warmup';
/**
 * Header that carries the warmup thread id on each pool-sourced send, so the
 * engager can look the thread up and reply with its matching contextual reply.
 * Absent on fallback (static-template) sends; the engager then falls back to a
 * generic static reply.
 */
export const WARMUP_THREAD_HEADER = 'X-RevBase-Warmup-Thread';
/**
 * Pick a pool thread deterministically by rotation index, mirroring the static
 * template rotation: the same (sender, peer, day, count) seed always maps to the
 * same thread, so successive sends fan out across the pool instead of repeating
 * one entry. Returns undefined when the pool is empty so the caller can fall
 * back to the static `WARMUP_TEMPLATES` path.
 */
export function pickWarmupThread(pool, rotation) {
    if (pool.length === 0)
        return undefined;
    const i = Math.abs(Math.floor(rotation)) % pool.length;
    return pool[i];
}
export const WARMUP_TEMPLATES = [
    {
        subject: 'quick note',
        body: 'Hey, just circling back on this. Let me know when you get a moment and we can pick it up. Thanks.',
    },
    {
        subject: 'following up',
        body: 'Wanted to follow up on the thread from earlier. No rush at all, just keeping it on my radar.',
    },
    {
        subject: 'checking in',
        body: 'Hope your week is going well. Flagging this so it does not slip. Happy to chat whenever works for you.',
    },
    {
        subject: 'about that',
        body: 'Thanks again for earlier. I had one more thought I wanted to share when you have a second.',
    },
    {
        subject: 'one more thing',
        body: 'Almost forgot to mention this. Nothing urgent, just wanted to get it down before I lost track of it.',
    },
    {
        subject: 'good to connect',
        body: 'Really good catching up. I will put together a few notes and send them along shortly. Talk soon.',
    },
    {
        subject: 'small update',
        body: 'Quick update on my end, things are moving along nicely. Will keep you posted as more comes together.',
    },
    {
        subject: 'thoughts',
        body: 'Had a couple of thoughts after our last exchange. Curious what you think when you get a chance to read.',
    },
];
/** A short, friendly reply body used when engaging a received warmup message. */
export const WARMUP_REPLY_BODIES = [
    'Thanks for this, sounds good to me. I will follow up soon.',
    'Got it, appreciate you sending it over. Talk shortly.',
    'Makes sense. Let me chew on it and circle back.',
    'Perfect, thanks for the note. Looking forward to it.',
];
/**
 * Pick a warmup template deterministically for a (sender, peer, index) tuple so
 * the same pair does not always get the same copy and the pool's traffic varies.
 */
export function pickWarmupTemplate(seed) {
    const i = Math.abs(Math.floor(seed)) % WARMUP_TEMPLATES.length;
    return WARMUP_TEMPLATES[i];
}
/** Pick a warmup reply body deterministically. */
export function pickWarmupReply(seed) {
    const i = Math.abs(Math.floor(seed)) % WARMUP_REPLY_BODIES.length;
    return WARMUP_REPLY_BODIES[i];
}
/** Default TTL (days) for a successful cached scrape (COLLATERAL_SPEC §4.4). */
export const SCRAPE_TTL_DAYS = 30;
/** Short TTL (days) for a cached failed scrape, so a broken site retries soon. */
export const SCRAPE_FAILURE_TTL_DAYS = 1;
/** The MVP singleton collateral doc id under a campaign. */
export const COLLATERAL_ID = 'current';
/**
 * Public landing path for a slug. Single source of truth for URL building, used
 * by the send-time merge var, the publish result, and the Hosting `/c/**` rewrite.
 */
export function landingPath(slug) {
    return `/c/${slug}`;
}
/**
 * Whether a cached scrape may be served without a re-scrape. `ok` honors the full
 * `SCRAPE_TTL_DAYS`; `failed` honors the short `SCRAPE_FAILURE_TTL_DAYS`; `pending`
 * and unknown states are never fresh. Pure so it is unit-testable, mirroring
 * `isCachedResultFresh` in the enrichment engine.
 */
export function isScrapeFresh(status, fetchedAt, now) {
    if (status === 'ok')
        return isEnrichmentFresh(fetchedAt, now, SCRAPE_TTL_DAYS);
    if (status === 'failed') {
        return isEnrichmentFresh(fetchedAt, now, SCRAPE_FAILURE_TTL_DAYS);
    }
    return false;
}
//# sourceMappingURL=models.js.map